import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    CheckCircle2, ArrowRight, Undo2, RefreshCw, Download, AlertTriangle,
    ExternalLink, ShieldCheck, Users, FileText, Info,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek } from '../constants';
import FilingSubmissionPanel from '../components/FilingSubmissionPanel';

// Skarpa filöverföringstjänsten (kräver e-legitimation). Verifierad mot
// skatteverket.se/filoverforing 2026-08-13 — länken "Inloggning" där.
// OBS: www1.skatteverket.se/fv/fv_web är TESTTJÄNSTEN ("Testa filer") —
// gamla länken hit öppnade dessutom en felsida (systemval.do utan session).
const FILOVERFORING_URL = 'https://sso.skatteverket.se/fv_ext/fv_web/login.do';

/**
 * INK4S-poster utöver 4.9/4.14/4.15, fråge­drivet grupperade (ÅO-mönstret).
 * Motorn (Ink4ComputationCore) har tagit emot samtliga sedan P6 — bara
 * inmatningen saknades (D16/D18, Codex R6). 4.3b/4.5b visas alltid: nästan
 * varje HB har någon ej avdragsgill kostnad eller ej skattepliktig intäkt.
 * Form: [gruppnamn, grupphjälp, [[key, label, help], …]]
 */
const INK4S_ALWAYS = [
    ['andraBokfordaKostnader', '4.3b Andra bokförda kostnader som inte ska dras av', 'T.ex. representation över schablonen, böter, medlemsavgifter, förseningsavgifter.'],
    ['andraBokfordaIntakter', '4.5b Andra bokförda intäkter som inte ska tas upp', 'T.ex. skattefria försäkringsersättningar och intäkter som beskattas hos delägarna direkt.'],
];
const INK4S_GROUPS = [
    ['Nedskrivningar och ackord (4.3a, 4.5a)', null, [
        ['nedskrivningFinansiella', '4.3a Nedskrivning av finansiella tillgångar', 'Ej avdragsgill — läggs tillbaka.'],
        ['ackordsvinster', '4.5a Ackordsvinster', 'Skattefria — dras av.'],
    ]],
    ['Ej bokförda poster (4.4, 4.6)', null, [
        ['skogsSubstansminskning', '4.4a Skogs-/substansminskningsavdrag (N8)', 'Enligt bilaga N8.'],
        ['andraEjBokfordaKostnader', '4.4b Andra ej bokförda kostnader', 'Avdragsgilla kostnader som inte bokförts.'],
        ['aterfordaVardeminskningFastighet', '4.6a Återförda värdeminskningsavdrag vid avyttring av fastighet', null],
        ['schablonintaktFondandelar', '4.6b Schablonintäkt på fondandelar', '0,4 % av fondandelarnas värde vid årets ingång.'],
        ['andraEjBokfordaIntakter', '4.6c Andra ej bokförda intäkter', 'Skattepliktiga intäkter som inte bokförts.'],
    ]],
    ['Delägarnas uttag och förmåner (4.7)',
        'Uttag av varor/tjänster som bokförts som intäkt — de beskattas i stället hos delägaren (N3A p. 4).', [
            ['uttagBokfordIntakt', '4.7 Delägarnas förmåner och uttag — bokförd intäkt av uttagen', null],
        ]],
    ['Andel i annat handelsbolag (4.8)',
        'Bara när bolaget självt är delägare i ett annat HB/KB. Mappa på fältkod, aldrig på gamla blankettbokstäver.', [
            ['andelHbBokfordIntakt', '4.8a Bokförd intäkt/vinst från andel i annat HB', null],
            ['n3bOverskott', '4.8b Skattemässigt överskott enligt N3B', null],
            ['andelHbBokfordKostnad', '4.8c Bokförd kostnad/förlust från andel i annat HB', null],
            ['n3bUnderskott', '4.8d Skattemässigt underskott enligt N3B', null],
        ]],
    ['Avyttring av fastighet och värdepapper (4.10–4.11)',
        'Vinster/förluster som beskattas hos delägarna (K7/K8 resp. N3A p. 6) och därför justeras bort här. Beloppen går vidare till delägarunderlaget.', [
            ['bokfordVinstFastighet', '4.10a Bokförd vinst, avyttrad näringsfastighet', null],
            ['bokfordForlustFastighet', '4.10b Bokförd förlust, avyttrad näringsfastighet', null],
            ['bokfordVinstVardepapper', '4.11a Bokförd vinst, avyttrade värdepapper', null],
            ['bokfordForlustVardepapper', '4.11b Bokförd förlust, avyttrade värdepapper', null],
        ]],
];
const INK4S_AMOUNT_KEYS = [
    'bokfordAvskrivningByggnadRestvarde', 'begartVardeminskningsavdrag',
    'ejAterfordaVardeminskningByggnader', 'ejAterfordaVardeminskningMark',
    ...INK4S_ALWAYS.map(([k]) => k),
    ...INK4S_GROUPS.flatMap(([, , fields]) => fields.map(([k]) => k)),
];

/**
 * Deklarationssteget för HANDELS-/KOMMANDITBOLAG — Inkomstdeklaration 4.
 *
 * Bolaget lämnar INK4R (räkenskapsschema), INK4S (skattemässiga justeringar)
 * och INK4DU (delägaruppgifter). Varje delägare lämnar sedan en egen
 * N3A-bilaga till sin inkomstdeklaration — den räknar vi inte åt dem, se
 * underlaget längst ned.
 *
 * Sidan 1 av INK4 skapas inte här: den är inte SRU-levererbar och behövs bara
 * om bolaget har eget underlag för särskild löneskatt, avkastningsskatt eller
 * fastighetsskatt/-avgift.
 */
const Ink4DeklarationStep = ({ companyId, processId, completed, onComplete, onUndo, busy, gateError }) => {
    const [preview, setPreview] = useState(null);
    const [n3a, setN3a] = useState(null);
    const [showAllFields, setShowAllFields] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generated, setGenerated] = useState(false);
    const [generationEvidence, setGenerationEvidence] = useState(null);
    const [filingConfirmed, setFilingConfirmed] = useState(false);
    const [openingAr, setOpeningAr] = useState(false);
    const [drafts, setDrafts] = useState({});
    const [savingDecisions, setSavingDecisions] = useState(false);

    const fetchAll = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setLoadError(null);
        try {
            const resp = await YearEndService.getInk4Preview(companyId, processId);
            setPreview(resp?.data || null);
            setDrafts(resp?.data?.decisions || {});
            try {
                const u = await YearEndService.getN3aUnderlag(companyId, processId);
                setN3a(u?.data || null);
            } catch { /* underlaget är sekundärt — blockera inte steget */ }
        } catch (err) {
            setLoadError(err.response?.data?.message
                || 'Kunde inte ta fram deklarationsunderlaget. Kontrollera att årets resultat är bokfört (steg 4).');
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId]);

    useEffect(() => { fetchAll({ silent: true }); }, [fetchAll]);

    const downloadBase64 = (filename, contentBase64) => {
        const bytes = Uint8Array.from(atob(contentBase64), (ch) => ch.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const saveDecisions = async (patch) => {
        setSavingDecisions(true);
        try {
            const next = { ...drafts, ...patch };
            setDrafts(next);
            // null, inte undefined: undefined faller bort i JSON och backend-
            // mergen behåller då det gamla sparade värdet (samma rensa-
            // släpper-aldrig-bugg som NE-steget, 2026-08-10).
            const num = (v) => (v === '' || v == null ? null
                : Number(String(v).replace(/\s/g, '').replace(',', '.')));
            const amounts = {};
            for (const key of INK4S_AMOUNT_KEYS) {
                if (key in next) amounts[key] = num(next[key]);
            }
            const resp = await YearEndService.saveInk4Decisions(companyId, processId, {
                ...next, ...amounts,
            });
            setPreview(resp?.data || null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte spara uppgifterna.');
        } finally { setSavingDecisions(false); }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const resp = await YearEndService.generateInk4Files(companyId, processId, {});
            (resp?.data?.files || []).forEach((f) => downloadBase64(f.filename, f.contentBase64));
            setGenerated(true);
            setGenerationEvidence(resp?.data?.evidence || null);
            setFilingConfirmed(false);
            toast.success(`SRU-filerna är skapade (${resp?.data?.delagare} delägaruppgifter).`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte skapa SRU-filerna.');
        } finally {
            setGenerating(false);
        }
    };

    const handleOpenArsbokslut = async () => {
        setOpeningAr(true);
        try {
            await YearEndService.openHbArsbokslutPdf(companyId, processId);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || 'Kunde inte skapa årsbokslutet.');
        } finally {
            setOpeningAr(false);
        }
    };

    const coverage = preview?.coverage;
    const canGenerate = Boolean(coverage?.complete && !preview?.scopeError
        && preview?.blocks?.INK4DU?.length && !generating
        && drafts.bitratt != null && drafts.revision != null
        && (!(preview?.delagarandring || []).length || drafts.fordelningAttested === true));

    return (
        <>
            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">
                        Deklarera (INK4)
                        {preview?.fiscalYear?.year ? ` — räkenskapsåret ${preview.fiscalYear.year}` : ''}
                    </h4>
                    <p className="section-subtitle">
                        Bolaget lämnar tre bilagor: INK4R (räkenskapsschemat, hämtat ur bokföringen),
                        INK4S (skattemässiga justeringar) och INK4DU (en uppgift per delägare).
                        Bolaget betalar ingen egen inkomstskatt — resultatet beskattas hos delägarna.
                    </p>
                    {/* SFL 33:10 → 32:2-tidpunkterna; helgdag flyttar fram fristen. */}
                    {preview?.deklarationsdeadline?.date && (
                        <p className="section-subtitle" style={{ marginTop: 4 }}>
                            <strong>Frist för särskilda uppgifter (INK4):</strong> senast{' '}
                            {preview.deklarationsdeadline.date} (SFL 33 kap. 10 § med 32 kap. 2 §-
                            tidpunkterna). Infaller dagen på helg flyttas fristen fram — se
                            Skatteverkets publicerade datum.
                        </p>
                    )}
                </div>
                <div className="action-buttons">
                    <button
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchAll()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'ye-spin' : undefined} />
                        Uppdatera
                    </button>
                </div>
            </div>

            <div className="modern-card-body">
            {loadError && (
                <div className="alert alert-warning d-flex align-items-center gap-2">
                    <AlertTriangle size={18} /><span>{loadError}</span>
                </div>
            )}

            {preview?.scopeError && (
                <div className="alert alert-danger d-flex align-items-center gap-2">
                    <AlertTriangle size={18} /><span>{preview.scopeError.message}</span>
                </div>
            )}

            {preview && !coverage?.complete && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">
                        <AlertTriangle size={16} style={{ marginRight: 6 }} />
                        {coverage.unmapped.length} konton saknar fältkod
                    </h5>
                    <p className="text-muted" style={{ maxWidth: '68ch' }}>
                        Dessa konton har saldon men ingår inte i BAS standardkoppling för INK4R.
                    </p>
                    <div className="ye-tax-rows">
                        {coverage.unmapped.slice(0, 12).map((row) => (
                            <div className="ye-tax-row" key={row.accountNumber}>
                                <span>{row.accountNumber} {row.accountName}</span>
                                <span className="ye-num">{formatSek(row.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {preview && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">Skattemässigt resultat</h5>
                    <div className="ye-tax-rows">
                        <div className="ye-tax-row">
                            <span>Bokfört resultat (INK4R 3.18/3.19)</span>
                            <span className="ye-num">{formatSek(preview.aretsResultat)}</span>
                        </div>
                        <div className="ye-tax-row ye-tax-row-group">
                            <span>Att fördela på delägarna (INK4S 4.12/4.13)</span>
                            <span className="ye-num">{formatSek(preview.resultatAttFordela)}</span>
                        </div>
                    </div>
                    <div className="ye-well" style={{ marginTop: '0.75rem' }}>
                        <Info size={14} style={{ marginRight: 6 }} />
                        Periodiseringsfond, expansionsfond och räntefördelning finns med flit inte
                        här. De är delägarnas egna avdrag och beräknas på deras N3A-bilagor —
                        de får varken bokföras i bolaget eller ingå i bolagets deklaration.
                    </div>
                </div>
            )}

            {preview?.blocks?.INK4DU?.length > 0 && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">
                        <Users size={16} style={{ marginRight: 6 }} />
                        Delägaruppgifter (INK4DU)
                    </h5>
                    <div className="ye-tax-rows">
                        {preview.blocks.INK4DU.map((b) => (
                            <div className="ye-tax-row" key={b.delagareId}>
                                <span>{b.personOrgNr}</span>
                                <span className="ye-num">{formatSek(b.andel)}</span>
                            </div>
                        ))}
                    </div>
                    {(preview.warnings || []).map((w, i) => (
                        <div className="alert alert-warning mt-2 mb-0" style={{ fontSize: '0.875rem' }} key={i}>{w}</div>
                    ))}
                    {(preview.delagarandring || []).length > 0 && (
                        <div className="alert alert-warning mt-2 mb-0" style={{ fontSize: '0.875rem' }}>
                            <label className="mb-0 d-flex align-items-start gap-2">
                                <input
                                    type="checkbox"
                                    checked={drafts.fordelningAttested === true}
                                    onChange={(e) => saveDecisions({ fordelningAttested: e.target.checked })}
                                    style={{ marginTop: 3 }}
                                />
                                <span>
                                    Jag intygar att de angivna resultatandelarna följer bolagsavtalet
                                    för räkenskapsåret, trots delägarändringen
                                    ({preview.delagarandring.join(', ')}). Krävs innan SRU-filerna
                                    kan skapas — systemet tidsviktar inte fördelningen.
                                </span>
                            </label>
                        </div>
                    )}
                </div>
            )}

            <div className="ye-ps-section">
                <h5 className="ye-section-heading">
                    <FileText size={16} style={{ marginRight: 6 }} />
                    Årsbokslut (BFNAR 2017:3)
                </h5>
                <p className="text-muted" style={{ maxWidth: '68ch' }}>
                    Årsbokslutet ska undertecknas av samtliga obegränsat ansvariga delägare och
                    sparas i sju år. Det lämnas <strong>inte in</strong> någonstans — det är
                    deklarationen som lämnas till Skatteverket.
                </p>
                <button
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                    onClick={handleOpenArsbokslut}
                    disabled={openingAr || !coverage?.complete}
                >
                    <FileText size={14} />
                    {openingAr ? 'Öppnar…' : 'Öppna årsbokslut'}
                </button>
            </div>

            <div className="ye-ps-section">
                <h5 className="ye-section-heading">Uppgifter till Skatteverket</h5>
                <div className="ye-ink2-question">
                    <span>
                        Har en uppdragstagare (t.ex. redovisningskonsult) biträtt vid
                        upprättandet av årsbokslutet?
                    </span>
                    <div className="btn-group btn-group-sm" role="group">
                        <button type="button"
                            className={`btn ${drafts.bitratt === true ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => saveDecisions({ bitratt: true })}>Ja</button>
                        <button type="button"
                            className={`btn ${drafts.bitratt === false ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => saveDecisions({ bitratt: false })}>Nej</button>
                    </div>
                </div>
                <div className="ye-ink2-question" style={{ marginTop: '0.5rem' }}>
                    <span>Har eller är årsbokslutet föremål för revision?</span>
                    <div className="btn-group btn-group-sm" role="group">
                        <button type="button"
                            className={`btn ${drafts.revision === true ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => saveDecisions({ revision: true })}>Ja</button>
                        <button type="button"
                            className={`btn ${drafts.revision === false ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => saveDecisions({ revision: false })}>Nej</button>
                    </div>
                </div>
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.8125rem' }}>
                    Båda frågorna ska besvaras i deklarationen — filerna kan inte skapas utan svar.
                </p>

                <div className="ye-form-grid" style={{ marginTop: '1rem' }}>
                    <div className="ye-field">
                        <label htmlFor="ink4-avskr-bokford">4.9 Bokförd avskrivning (pluspost)</label>
                        <input id="ink4-avskr-bokford" inputMode="decimal" placeholder="0"
                            value={drafts.bokfordAvskrivningByggnadRestvarde ?? ''}
                            onChange={(e) => setDrafts({ ...drafts, bokfordAvskrivningByggnadRestvarde: e.target.value })}
                            onBlur={() => saveDecisions({})} />
                        <div className="ye-field-help">
                            Byggnad/fast egendom samt restvärdesavskrivning. Fylls i tillsammans med
                            begärt värdeminskningsavdrag — det är två poster, inte ett netto.
                        </div>
                    </div>
                    <div className="ye-field">
                        <label htmlFor="ink4-avskr-begart">4.9 Begärt värdeminskningsavdrag (minuspost)</label>
                        <input id="ink4-avskr-begart" inputMode="decimal" placeholder="0"
                            value={drafts.begartVardeminskningsavdrag ?? ''}
                            onChange={(e) => setDrafts({ ...drafts, begartVardeminskningsavdrag: e.target.value })}
                            onBlur={() => saveDecisions({})} />
                    </div>
                    <div className="ye-field">
                        <label htmlFor="ink4-8020">4.14 Ej återförda värdeminskningsavdrag, byggnader</label>
                        <input id="ink4-8020" inputMode="decimal" placeholder="0"
                            value={drafts.ejAterfordaVardeminskningByggnader ?? ''}
                            onChange={(e) => setDrafts({ ...drafts, ejAterfordaVardeminskningByggnader: e.target.value })}
                            onBlur={() => saveDecisions({})} />
                        <div className="ye-field-help">
                            Sammanlagda avdrag fram till årets utgång som ska återföras vid försäljning.
                            Påverkar inte resultatet.
                        </div>
                    </div>
                    <div className="ye-field">
                        <label htmlFor="ink4-8021">4.15 Ej återförda värdeminskningsavdrag, markanläggningar</label>
                        <input id="ink4-8021" inputMode="decimal" placeholder="0"
                            value={drafts.ejAterfordaVardeminskningMark ?? ''}
                            onChange={(e) => setDrafts({ ...drafts, ejAterfordaVardeminskningMark: e.target.value })}
                            onBlur={() => saveDecisions({})} />
                    </div>
                    {INK4S_ALWAYS.map(([key, label, help]) => (
                        <div className="ye-field" key={key}>
                            <label htmlFor={`ink4-${key}`}>{label}</label>
                            <input id={`ink4-${key}`} inputMode="decimal" placeholder="0"
                                value={drafts[key] ?? ''}
                                onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
                                onBlur={() => saveDecisions({})} />
                            {help && <div className="ye-field-help">{help}</div>}
                        </div>
                    ))}
                </div>
                {INK4S_GROUPS.map(([groupName, groupHelp, fields]) => {
                    if (!showAllFields && !fields.some(([key]) => drafts[key])) return null;
                    return (
                        <div key={groupName} style={{ marginTop: '0.75rem' }}>
                            <h6 style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>{groupName}</h6>
                            {groupHelp && <div className="ye-field-help" style={{ marginBottom: '0.5rem' }}>{groupHelp}</div>}
                            <div className="ye-form-grid">
                                {fields.map(([key, label, help]) => (
                                    <div className="ye-field" key={key}>
                                        <label htmlFor={`ink4-${key}`}>{label}</label>
                                        <input id={`ink4-${key}`} inputMode="decimal" placeholder="0"
                                            value={drafts[key] ?? ''}
                                            onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
                                            onBlur={() => saveDecisions({})} />
                                        {help && <div className="ye-field-help">{help}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
                <button
                    type="button"
                    className="btn btn-link btn-sm px-0"
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => setShowAllFields((v) => !v)}
                >
                    {showAllFields
                        ? 'Visa bara de vanligaste posterna'
                        : 'Visa fler poster (andel i annat HB, fastighets-/värdepappersavyttring, ej bokförda poster m.m.)'}
                </button>
                <div className="ye-ink2-question" style={{ marginTop: '0.75rem' }}>
                    <span>Lämnas övriga upplysningar i särskild skrivelse (fältkod 90)?</span>
                    <div className="btn-group btn-group-sm" role="group">
                        <button type="button"
                            className={`btn ${drafts.ovrigaUpplysningar === true ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => saveDecisions({ ovrigaUpplysningar: true })}>Ja</button>
                        <button type="button"
                            className={`btn ${drafts.ovrigaUpplysningar !== true ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => saveDecisions({ ovrigaUpplysningar: false })}>Nej</button>
                    </div>
                </div>
                {savingDecisions && (
                    <span className="text-muted" style={{ fontSize: '0.8125rem' }}>Sparar…</span>
                )}
            </div>

            <div className="ye-ps-section">
                <h5 className="ye-section-heading">Skapa filerna</h5>
                <div className="ye-proposal-actions">
                    <button
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={handleGenerate}
                        disabled={!canGenerate}
                    >
                        <Download size={16} />
                        {generating ? 'Skapar filer…' : 'Skapa och ladda ner SRU-filer'}
                    </button>
                </div>
            </div>

            {n3a && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">Underlag till delägarnas N3A</h5>
                    <p className="text-muted" style={{ maxWidth: '68ch' }}>{n3a.note}</p>
                    <div className="ye-tax-rows">
                        {n3a.delagare.map((d) => (
                            <div className="ye-tax-row" key={d.delagareId}>
                                <span>{d.namn} <span className="text-muted">({d.personOrgNr})</span></span>
                                <span className="ye-num">
                                    {formatSek(d.andelOverskott || -d.andelUnderskott)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="ye-well" style={{ marginTop: '0.75rem' }}>
                        Varje delägare fyller själv i följande på sin N3A — de bygger på delägarens
                        egen justerade anskaffningsutgift och går inte att härleda ur bolagets
                        bokföring:
                        <ul>
                            {(n3a.egetAnsvar || []).map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                    </div>
                </div>
            )}

            <div className="ye-ps-section">
                <h5 className="ye-section-heading">
                    <ShieldCheck size={16} style={{ marginRight: 6 }} />
                    Så lämnar du in
                </h5>
                <ol className="mb-2" style={{ maxWidth: '68ch' }}>
                    <li>Ladda ner <code>info.sru</code> och <code>blanketter.sru</code> ovan.</li>
                    <li>Ladda upp båda filerna i Skatteverkets filöverföringstjänst.</li>
                    <li>Kontrollera kvittensen — först då är uppgifterna inlämnade.</li>
                    <li>Varje delägare lämnar sedan sin egen N3A-bilaga med sin inkomstdeklaration.</li>
                </ol>
                <a
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                    href={FILOVERFORING_URL} target="_blank" rel="noopener noreferrer"
                >
                    Öppna Skatteverkets filöverföring
                    <ExternalLink size={14} />
                </a>
            </div>

            {gateError && (
                <div className="alert alert-danger d-flex align-items-center gap-2">
                    <AlertTriangle size={18} /><span>{gateError.message}</span>
                </div>
            )}

            {!completed && (
                <FilingSubmissionPanel
                    companyId={companyId}
                    processId={processId}
                    generatedEvidence={generationEvidence}
                    onStatusChange={setFilingConfirmed}
                />
            )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Deklarationen är markerad som inlämnad
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo} disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {generated
                                ? 'När du fått Skatteverkets kvittens — markera steget som klart.'
                                : 'Skapa filerna och lämna in dem hos Skatteverket först.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete} disabled={busy || !filingConfirmed}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default Ink4DeklarationStep;
